import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { useProdutos } from "@/features/produtos/hooks/useProdutos";
import { useDebounce } from "@/hooks/useDebounce";
import { usePaginacaoServidor } from "@/hooks/usePaginacaoServidor";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

interface ProductTableProps {
  searchTerm: string;
}

/**
 * Produtos da tela Gestão de Estoque. Busca e paginação no SERVIDOR (antes filtrava
 * só os 20 primeiros no cliente). Edição/exclusão ficam no cadastro de produto.
 */
export function ProductTable({ searchTerm }: ProductTableProps) {
  const navigate = useNavigate();
  const termo = useDebounce(searchTerm.trim(), 350);
  const pag = usePaginacaoServidor({ resetarQuando: [termo] });

  const { produtos, total, isLoading } = useProdutos({
    pagina: pag.pagina,
    tamanhoPagina: pag.tamanhoPagina,
    filtros: { termo: termo || undefined, ordenarPor: "descricao" },
  });

  const totalPaginas = Math.max(1, Math.ceil(total / pag.tamanhoPagina));

  return (
    <div className="space-y-2">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código de Barras</TableHead>
              <TableHead>Produto</TableHead>
              <TableHead>Descrição Reduzida</TableHead>
              <TableHead className="text-right">Preço</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Carregando produtos...
                </TableCell>
              </TableRow>
            ) : produtos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Nenhum produto encontrado
                </TableCell>
              </TableRow>
            ) : (
              produtos.map((produto) => (
                <TableRow
                  key={produto.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/produtos/${produto.id}`)}
                >
                  <TableCell className="font-mono text-sm">{produto.codigoBarras || "-"}</TableCell>
                  <TableCell className="font-medium">{produto.descricao}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{produto.descricaoReduzida || "-"}</TableCell>
                  <TableCell className="text-right font-medium">
                    {produto.precoPadrao.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={produto.inativo ? "secondary" : "default"}>
                      {produto.inativo ? "Inativo" : "Ativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Abrir cadastro do produto"
                      onClick={(e) => { e.stopPropagation(); navigate(`/produtos/${produto.id}`); }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />
    </div>
  );
}
